import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {NucleotideType} from 'biojs/Util';
import ChemicalStructureSVG from 'chemistry/ChemicalStructureSVG';
import {PositionalRule} from 'constants/SequenceOpConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  BiointelligentLinkContainerCountQuery,
  BiointelligentLinkContainerCountQueryVariables,
} from 'molbio/biointelligent-links/__generated/BiointelligentLinkContainerCountQuery';
import {BIOINTELLIGENT_LINK_CONTAINER_COUNT_QUERY} from 'molbio/biointelligent-links/BiointelligentLinkQueries';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import Link from 'ui-library/Link';
import Modal from 'ui-library/modals/Modal';
import {capitalize} from 'util/NameUtil';

import {MonomerQuery} from './__generated/MonomerQuery';
import {
  DEGENERATE_BASE,
  MissingMonomerReason,
  MONOMER_ANALOGUE_DISPLAY_NAME,
  MONOMER_QUERY,
  NucleotideComponent,
} from './MonomerConstants';
import {SelfOpeningMonomerDropdown, SpecialMonomerValue} from './MonomerDropdown';

type Monomer = MonomerQuery['monomers'][number];

function MonomerRow({
  monomerId,
  monomersById,
  nucleotideComponent,
  nucleotideType,
  onUpdateMonomerId,
}: {
  monomerId: string | MissingMonomerReason;
  monomersById: {[id: string]: Monomer};
  nucleotideComponent: NucleotideComponent;
  nucleotideType: NucleotideType;
  onUpdateMonomerId: (id: string) => void;
}): JSX.Element {
  const shouldShowMonomerInfo = !Object.keys(MissingMonomerReason).includes(monomerId);
  return (
    <tr
      {...getTestProps({component: 'NucleotideEditModal', element: 'monomerRow', key: nucleotideComponent})}
      data-amp-safe={false}
    >
      <td className={css(styles.cell)}>{capitalize(nucleotideComponent)}</td>
      <td className={css(styles.cell)}>
        <SelfOpeningMonomerDropdown
          nucleotideType={nucleotideType}
          selectedMonomerId={monomerId}
          nucleotideComponent={nucleotideComponent}
          updateSelectedMonomerId={onUpdateMonomerId}
          nameMaxLength={30}
        />
      </td>
      <td className={css(styles.cell)}>{shouldShowMonomerInfo ? monomersById[monomerId].symbol : '-'}</td>
      <td className={css(styles.cell)}>
        {shouldShowMonomerInfo ? MONOMER_ANALOGUE_DISPLAY_NAME[monomersById[monomerId].naturalAnalogue] : '-'}
      </td>
      <td className={css(styles.cell)}>
        {shouldShowMonomerInfo ? (
          <ChemicalStructureSVG
            chemicalStructureImage={monomersById[monomerId].chemicalStructureImage}
            svgClassName={css(styles.chemicalStructureSvg)}
          />
        ) : (
          '-'
        )}
      </td>
    </tr>
  );
}

export interface NucleotidesEditModalProps {
  initialBaseId: string | MissingMonomerReason;
  initialPhosphateId: string | MissingMonomerReason;
  initialSugarId: string | MissingMonomerReason;
  isPartSequence?: boolean;
  monomersById: {[id: string]: Monomer};
  nucleotideType: NucleotideType;
  onClose?: () => void;
  onSubmit: (rule: PositionalRule) => void;
  positions: Set<number>;
}

export default function NucleotidesEditModal(props: NucleotidesEditModalProps): JSX.Element {
  const [selectedSugarId, setSelectedSugarId] = useState(props.initialSugarId);
  const [selectedBaseId, setSelectedBaseId] = useState(props.initialBaseId);
  const [selectedPhosphateId, setSelectedPhosphateId] = useState(props.initialPhosphateId);

  return (
    <Modal
      title="Edit Nucleotides"
      large={true}
      buttons={[
        {text: 'Cancel', cancel: true},
        {
          text: 'Save',
          submit: true,
          disabled:
            props.isPartSequence ||
            [selectedSugarId, selectedBaseId, selectedPhosphateId].every(
              (monomerId) => monomerId === MissingMonomerReason.CHOOSING
            ),
        },
      ]}
      onRequestClose={() => {
        if (props.onClose) {
          props.onClose();
        }
        LegacyModalContainer.close(NucleotidesEditModal);
      }}
      onRequestSubmit={() => {
        props.onSubmit({
          target: {
            positions: [...props.positions],
          },
          replacement: {
            sugarMonomerId:
              selectedSugarId === MissingMonomerReason.CHOOSING
                ? SpecialMonomerValue.EXISTING_MONOMER
                : selectedSugarId,
            baseMonomerId:
              selectedBaseId === MissingMonomerReason.CHOOSING
                ? SpecialMonomerValue.EXISTING_MONOMER
                : selectedBaseId === MissingMonomerReason.DEGENERATE
                  ? DEGENERATE_BASE
                  : selectedBaseId,
            phosphateMonomerId:
              selectedPhosphateId === MissingMonomerReason.CHOOSING
                ? SpecialMonomerValue.EXISTING_MONOMER
                : selectedPhosphateId,
          },
        });
        LegacyModalContainer.close(NucleotidesEditModal);
      }}
      footer={
        <Link href="/monomers" target="_blank">
          Go to nucleotide monomer library
        </Link>
      }
    >
      {props.isPartSequence && (
        <div className="u-small-spacing-bottom">
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text="Chemical modifications are not permitted on sequences referenced as parts."
          />
        </div>
      )}
      <div>
        You are editing nucleotides at the following positions: {}
        {Array.from(props.positions)
          .sort((a, b) => a - b)
          .map((p) => p + 1)
          .join(', ')}
      </div>
      <br />
      <table className="table">
        <thead>
          <tr>
            <td width="15%">
              <b>Type</b>
            </td>
            <td width="30%">
              <b>Monomer</b>
            </td>
            <td width="15%">
              <b>Symbol</b>
            </td>
            <td width="10%">
              <b>Analogue</b>
            </td>
            <td width="30%">
              <b>Chemical structure</b>
            </td>
          </tr>
        </thead>
        <tbody>
          <MonomerRow
            monomersById={props.monomersById}
            monomerId={selectedSugarId}
            nucleotideType={props.nucleotideType}
            nucleotideComponent={NucleotideComponent.SUGAR}
            onUpdateMonomerId={setSelectedSugarId}
          />
          <MonomerRow
            monomersById={props.monomersById}
            monomerId={selectedBaseId}
            nucleotideType={props.nucleotideType}
            nucleotideComponent={NucleotideComponent.BASE}
            onUpdateMonomerId={setSelectedBaseId}
          />
          <MonomerRow
            monomersById={props.monomersById}
            monomerId={selectedPhosphateId}
            nucleotideType={props.nucleotideType}
            nucleotideComponent={NucleotideComponent.PHOSPHATE}
            onUpdateMonomerId={setSelectedPhosphateId}
          />
        </tbody>
      </table>
    </Modal>
  );
}

function groupMonomersById(monomers: Array<Monomer>): {[id: string]: Monomer} {
  const monomersById: {[id: string]: Monomer} = {};
  for (const monomer of monomers) {
    monomersById[monomer.id] = monomer;
  }
  return monomersById;
}

interface QueryingNucleotidesEditModalProps extends Omit<
  NucleotidesEditModalProps,
  'monomersById' | 'isPartSequence'
> {
  sequenceId: string;
}

export function QueryingNucleotidesEditModal(props: QueryingNucleotidesEditModalProps): JSX.Element {
  return (
    <BenchlingQuery<BiointelligentLinkContainerCountQuery, BiointelligentLinkContainerCountQueryVariables>
      query={BIOINTELLIGENT_LINK_CONTAINER_COUNT_QUERY}
      renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
      variables={{id: props.sequenceId}}
    >
      {(bioentityData) => {
        const polymer = bioentityData.polymer;
        return (
          <BenchlingQuery<MonomerQuery> query={MONOMER_QUERY} renderLoading={() => null}>
            {({monomers}) => (
              <NucleotidesEditModal
                isPartSequence={!isInaccessible(polymer) && polymer.partContainerCount > 0}
                monomersById={groupMonomersById(monomers)}
                {...props}
              />
            )}
          </BenchlingQuery>
        );
      }}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  cell: {
    verticalAlign: 'middle',
  },
  chemicalStructureSvg: {
    height: '80px',
    maxWidth: '180px',
  },
});
